import { Stack } from "@mui/material";
import { useTimelineZoom } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { useVideoControlActions } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import {
  VideoSuspensionProvider,
  useVideoSuspended,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoSuspensionContext";
import {
  AreaMaterialSnapshot,
  ScrapEyesEvent,
} from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import { useLowMemoryVideoMode } from "hooks/settings";
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { LabellerTimeline, ReviewPeriodData } from "src/store/api/generatedApi";
import { parseFocusTimestampParam } from "./utils/timeUtils";
import { EditEventModal } from "./editModal/EditEventModal";
import {
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "./contexts/ManualReviewEventContext";
import { useManualReviewVideoPlayerState } from "./hooks/useManualReviewVideoPlayerState";
import { ManualReviewVideoControls } from "./ManualReviewVideoControls";
import { getEventToLabellerMapping } from "./utils/eventUtils";
import { ToggleableAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/ToggleableAnglesVideoPlayer";
import { PLAYBACK_SCOPE } from "components/scrapEyeDataDashboard/VideoPlayer/constants";

interface ManualReviewVideoPlayerProps {
  location: string;
  footageLocation?: string;
  start: Dayjs;
  end: Dayjs;
  events: ScrapEyesEventWithReviewData[];
  reviewPeriods: ReviewPeriodData[];
  reviewPeriodId: number | null;
  isLoading?: boolean;
  isEditMode?: boolean;
  actionButtons?: ReactNode;
  labellerTimelines: LabellerTimeline[];
  areaMaterialSnapshots?: AreaMaterialSnapshot[];
  onMappingSnapshotClick?: (snapshot: AreaMaterialSnapshot) => void;
  selectedSnapshotTimestamp?: number | null;
  areaColors?: Record<string, string>;
  // Set only when rendered inside the linked-period bring-in modal: the periods this save
  // will verify. Its presence switches the period bars to the bring-in scope indicator.
  bringInWillVerifyPeriodIds?: readonly number[];
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
}

export const ManualReviewVideoPlayer = ({
  location,
  footageLocation,
  start,
  end,
  reviewPeriods,
  isLoading,
  isEditMode,
  actionButtons,
  labellerTimelines,
  areaMaterialSnapshots,
  onMappingSnapshotClick,
  selectedSnapshotTimestamp,
  areaColors,
  bringInWillVerifyPeriodIds,
  highlightColorsByEventId,
}: Omit<ManualReviewVideoPlayerProps, "events" | "reviewPeriodId">) => {
  const {
    allEvents,
    recordingEventId,
    handleUpdateEvent,
    handleDeleteEvent,
    writableStart,
    writableEnd,
    canWrite,
  } = useManualReviewEvents();

  const eventToLabellerMapping = useMemo(() => {
    return getEventToLabellerMapping(labellerTimelines);
  }, [labellerTimelines]);

  const zoomContext = useTimelineZoom();
  const visibleStart = zoomContext?.visibleStart ?? start;
  const visibleEnd = zoomContext?.visibleEnd ?? end;

  const eventsInView = useMemo(() => {
    return allEvents.filter((event: ScrapEyesEvent) => {
      return (
        event.start.isBefore(visibleEnd) && event.end.isAfter(visibleStart)
      );
    });
  }, [allEvents, visibleStart, visibleEnd]);

  const {
    isModalOpen,
    selectedEvent,
    selectEvent,
    handleModalClose,
    navigatePrevious,
    navigateNext,
    hasPrevious,
    hasNext,
  } = useManualReviewVideoPlayerState({
    events: eventsInView,
    videoStart: start,
  });

  const [searchParams] = useSearchParams();
  const focusTimestamp = useMemo(
    () => parseFocusTimestampParam(searchParams.get("date")),
    [searchParams]
  );
  const hasSeekedToFocusRef = useRef(false);

  const actions = useVideoControlActions();

  useEffect(() => {
    actions.reset();
    const duration = end.diff(start, "second");
    actions.updateDuration(duration);

    // On a deep link, seek once to the focussed moment so the first frame shown
    // is the linked instant rather than the window's left edge.
    if (
      !hasSeekedToFocusRef.current &&
      focusTimestamp &&
      !focusTimestamp.isBefore(start) &&
      !focusTimestamp.isAfter(end)
    ) {
      actions.seekTo(focusTimestamp.diff(start, "second"));
      hasSeekedToFocusRef.current = true;
    }
  }, [end, start, actions, focusTimestamp]);

  const handleConflictClick = useCallback(
    (eventId: string) => {
      selectEvent(eventId, "conflicts");
    },
    [selectEvent]
  );

  const handleEventClick = useCallback(
    (eventId: string) => {
      selectEvent(eventId, "single");
    },
    [selectEvent]
  );

  const handleDeleteEventFromModal = useCallback(() => {
    if (selectedEvent) {
      handleDeleteEvent(selectedEvent.id);
      handleModalClose();
    }
  }, [selectedEvent, handleDeleteEvent, handleModalClose]);

  const handleNavigatePrevious = hasPrevious ? navigatePrevious : undefined;
  const handleNavigateNext = hasNext ? navigateNext : undefined;

  // The modal opens read-only for an event outside the writable window, and for
  // a verified event until edit mode is entered.
  const selectedEventReadOnlyReason = useMemo(():
    "verified" | "outsideWindow" | null => {
    if (!selectedEvent) return null;
    if (!canWrite(selectedEvent.id)) return "outsideWindow";
    if (isEditMode) return null;
    const inVerifiedPeriod = reviewPeriods.some((period) => {
      if (period.verified_at == null) return false;
      const periodStart = dayjs(period.start);
      const periodEnd = dayjs(period.end);
      return (
        !selectedEvent.start.isBefore(periodStart) &&
        selectedEvent.start.isBefore(periodEnd)
      );
    });
    return inVerifiedPeriod ? "verified" : null;
  }, [isEditMode, selectedEvent, reviewPeriods, canWrite]);

  const suspendedByParent = useVideoSuspended();
  const releaseBehindDialog = useLowMemoryVideoMode() && isModalOpen;

  return (
    <>
      <Stack spacing={2} sx={{ flex: 1 }}>
        <VideoSuspensionProvider
          suspended={suspendedByParent || releaseBehindDialog}
        >
          <ToggleableAnglesVideoPlayer
            hotkeyScope={PLAYBACK_SCOPE}
            storageKey="manualReview-video-player"
            location={footageLocation ?? location}
            badgeLocationName={
              footageLocation === location ? undefined : footageLocation
            }
            startIso={start.toISOString()}
            endIso={end.toISOString()}
            showVideoControls={false}
          />
        </VideoSuspensionProvider>

        <ManualReviewVideoControls
          events={eventsInView}
          location={location}
          reviewPeriods={reviewPeriods}
          recordingEventId={recordingEventId ?? undefined}
          onEventClick={handleEventClick}
          videoStart={start}
          actionButtons={actionButtons}
          duration={end.diff(start, "second")}
          onConflictClick={handleConflictClick}
          labellerTimelines={labellerTimelines}
          areaMaterialSnapshots={areaMaterialSnapshots}
          onMappingSnapshotClick={onMappingSnapshotClick}
          selectedSnapshotTimestamp={selectedSnapshotTimestamp}
          isLoading={isLoading}
          isEditMode={isEditMode}
          bringInWillVerifyPeriodIds={bringInWillVerifyPeriodIds}
          highlightColorsByEventId={highlightColorsByEventId}
        />
      </Stack>

      <EditEventModal
        open={isModalOpen}
        onClose={handleModalClose}
        mergedEvent={selectedEvent}
        eventToLabellerMapping={eventToLabellerMapping}
        location={location}
        onUpdate={(updates, materialEditScope) => {
          if (selectedEvent) {
            handleUpdateEvent(selectedEvent.id, updates, materialEditScope);
          }
        }}
        onDelete={handleDeleteEventFromModal}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        areaColors={areaColors}
        readOnly={selectedEventReadOnlyReason !== null}
        readOnlyReason={selectedEventReadOnlyReason ?? undefined}
        minTimingBound={writableStart}
        maxTimingBound={writableEnd}
      />
    </>
  );
};
